import { FC, memo, useCallback, useState } from 'react'
import { CrewScrollView } from 'components/devextreme/crewScrollView'
import { useTranslation } from '@crew/modules/i18n'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import { CrewErrorBoundary } from 'components/functions/crewErrorBoundary'
import { GroupChatMemberList } from './components/groupChatMemberList/groupChatMemberList'
import { useAppDispatch } from 'states/hooks'
import { setSearchKeyword } from '../../states/groupChatAddMemberDialogSlice'
import { GroupChatMemberToolbar } from './components/groupChatMemberToolbar/groupChatMemberToolbar'

type GroupChatMemberPanelProps = {
  projectId: string
  onAddUsers: (userIds: string[]) => void
  onClose: () => void
}

// ユーザー追加
export const GroupChatMemberPanel: FC<GroupChatMemberPanelProps> = memo(
  (props) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()

    // 選択されたユーザIDリスト
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([])

    // ユーザ選択状態変更時
    const handleUserSelectionChange = useCallback(
      (userId: string, checked: boolean) => {
        setSelectedUserIds((prev) => {
          if (checked) {
            // 既に配列に存在するかチェック
            if (prev.includes(userId)) {
              return prev // 既に存在する場合は変更しない
            }
            // チェックされた場合、配列に追加
            return [...prev, userId]
          } else {
            // チェック解除された場合、配列から削除
            return prev.filter((id) => id !== userId)
          }
        })
      },
      []
    )

    // 閉じるボタンクリック時
    const handleCloseButtonClick = useCallback(() => {
      dispatch(setSearchKeyword(''))
      props.onClose()
    }, [dispatch, props])

    // 追加ボタンクリック時
    const handleAddButtonClick = useCallback(async () => {
      // 追加処理を親コンポーネントに通知
      props.onAddUsers(selectedUserIds)
      // 検索キーワードをクリアして閉じる
      dispatch(setSearchKeyword(''))
      props.onClose()
    }, [dispatch, props, selectedUserIds])

    return (
      <div className="flex flex-col h-full gap-y-2">
        {/* ツールバー */}
        <CrewErrorBoundary>
          <GroupChatMemberToolbar />
        </CrewErrorBoundary>

        {/* ユーザー一覧 */}
        <CrewScrollView className="flex-1 min-h-0">
          <div className="flex flex-col gap-y-5">
            {/* モーダルの最小幅を制限し、ユーザ一覧が正しく表示されるようにする */}
            <div className="overflow-x-auto">
              <div className="min-w-[400px] flex flex-col gap-y-2.5">
                <GroupChatMemberList
                  projectId={props.projectId}
                  onUserSelected={handleUserSelectionChange}
                  selectedUserIds={selectedUserIds}
                />
              </div>
            </div>
          </div>
        </CrewScrollView>

        <div className="flex justify-end gap-x-2">
          {/* 追加ボタン */}
          <CrewButton
            text={t('action.add')}
            type="primary"
            stylingMode="contained"
            onClick={handleAddButtonClick}
          />
          {/* 閉じるボタン */}
          <CrewButton
            text={t('action.close')}
            type="normal"
            stylingMode="outlined"
            onClick={handleCloseButtonClick}
          />
        </div>
      </div>
    )
  }
)
